import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Globe, Trash2, Mail } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ImageUpload from "../components/ImageUpload";
import WebCoursesTab from "../components/WebCoursesTab";

const TABS = [
  ["general", "General Information"],
  ["homepage", "Home Page"],
  ["courses", "Courses"],
  ["about", "About Us"],
  ["sections", "Website Sections"],
  ["enquiries", "Enquiries"],
];

const SECTION_TOGGLES = [
  ["courses", "Courses page"],
  ["verification", "Verification page"],
  ["about", "About Us page"],
  ["contact", "Contact Us page"],
];

export default function Website() {
  const [settings, setSettings] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("general");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data: st }, { data: enq }] = await Promise.all([
      api.get("/settings"),
      api.get("/enquiries"),
    ]);
    setSettings(st);
    setEnquiries(enq);
  };
  useEffect(() => {
    load();
  }, []);

  if (!settings) return <div className="p-10 text-slate-400 text-sm">Loading…</div>;

  const upd = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const sections = settings.website_sections || {};
  const toggleSection = (k) =>
    upd("website_sections", { ...sections, [k]: sections[k] === false });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      toast.success("Website settings saved — public site updated");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const delEnquiry = async (id) => {
    await api.delete(`/enquiries/${id}`);
    setEnquiries((list) => list.filter((e) => e.id !== id));
    toast.success("Enquiry deleted");
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="website-title">
            Website Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Content saved here is published instantly on the public website (Home, Courses, About, Contact).
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="website-save"
          className="bg-blue-800 hover:bg-blue-900 h-10">
          {saving ? <Loader2 size={15} className="animate-spin mr-2" /> : <Save size={15} className="mr-2" />}
          Save & Publish
        </Button>
      </div>

      <div className="flex gap-1.5 mb-6 flex-wrap" data-testid="ws-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`ws-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 h-9 rounded-md text-sm font-medium transition-soft ${
              tab === k ? "bg-blue-800 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300"
            }`}>
            {label}
            {k === "enquiries" && enquiries.length > 0 && (
              <span className="ml-1.5 text-[11px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">{enquiries.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "general" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5" data-testid="ws-general">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Institute name">
              <Input value={settings.institute_name || ""} data-testid="ws-institute-name" className="h-10"
                onChange={(e) => upd("institute_name", e.target.value)} />
            </Field>
            <Field label="Registration / recognition info">
              <Input value={settings.registration_info || ""} data-testid="ws-registration" className="h-10"
                onChange={(e) => upd("registration_info", e.target.value)} />
            </Field>
          </div>
          <Field label="Address">
            <Input value={settings.address || ""} data-testid="ws-address" className="h-10"
              onChange={(e) => upd("address", e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Phone 1">
              <Input value={settings.phone || ""} data-testid="ws-phone" className="h-10"
                onChange={(e) => upd("phone", e.target.value)} />
            </Field>
            <Field label="Phone 2">
              <Input value={settings.phone2 || ""} data-testid="ws-phone2" className="h-10"
                onChange={(e) => upd("phone2", e.target.value)} />
            </Field>
            <Field label="Email">
              <Input value={settings.email || ""} data-testid="ws-email" className="h-10"
                onChange={(e) => upd("email", e.target.value)} />
            </Field>
            <Field label="Website">
              <Input value={settings.website || ""} data-testid="ws-website" className="h-10"
                onChange={(e) => upd("website", e.target.value)} />
            </Field>
            <Field label="Office hours">
              <Input value={settings.office_hours || ""} data-testid="ws-hours" className="h-10"
                placeholder="e.g., Mon–Sat, 9 AM – 6 PM"
                onChange={(e) => upd("office_hours", e.target.value)} />
            </Field>
          </div>
          <ImageUpload label="Institute logo (website header, footer & certificates)"
            value={settings.logo} onChange={(v) => upd("logo", v)} testId="ws-logo" />
        </div>
      )}

      {tab === "homepage" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5" data-testid="ws-homepage">
          <Field label="Hero heading (empty = “Welcome to {Institute Name}”)">
            <Input value={settings.hero_heading || ""} data-testid="ws-hero-heading" className="h-10"
              onChange={(e) => upd("hero_heading", e.target.value)} />
          </Field>
          <Field label="Hero introduction">
            <Textarea value={settings.hero_intro || ""} data-testid="ws-hero-intro" rows={3}
              placeholder="Quality Education • Professional Courses • Recognized Certification"
              onChange={(e) => upd("hero_intro", e.target.value)} />
          </Field>
        </div>
      )}

      {tab === "courses" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="ws-courses-section">
          <WebCoursesTab />
        </div>
      )}

      {tab === "about" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5" data-testid="ws-about">
          <Field label="About the institute">
            <Textarea value={settings.about_text || ""} data-testid="ws-about-text" rows={5}
              onChange={(e) => upd("about_text", e.target.value)} />
          </Field>
          <Field label="Mission">
            <Textarea value={settings.mission || ""} data-testid="ws-mission" rows={3}
              onChange={(e) => upd("mission", e.target.value)} />
          </Field>
          <Field label="Vision">
            <Textarea value={settings.vision || ""} data-testid="ws-vision" rows={3}
              onChange={(e) => upd("vision", e.target.value)} />
          </Field>
        </div>
      )}

      {tab === "sections" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="ws-sections">
          <p className="text-sm text-slate-500 mb-4">
            Enable or disable public website sections. Disabled sections disappear from the website navigation.
          </p>
          <div className="space-y-3">
            {SECTION_TOGGLES.map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 text-sm text-slate-700">
                <input type="checkbox" data-testid={`ws-sec-${k}`}
                  checked={sections[k] !== false}
                  onChange={() => toggleSection(k)}
                  className="w-4 h-4 accent-blue-800" />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {tab === "enquiries" && (
        <div className="space-y-3" data-testid="ws-enquiries">
          {enquiries.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-400">
              <Mail className="mx-auto mb-2 text-slate-300" size={26} />
              No enquiries yet — messages from the Contact Us form appear here.
            </div>
          )}
          {enquiries.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`enquiry-card-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{e.name}
                    {e.course && (
                      <span className="ml-2 text-[11px] font-medium bg-blue-100 text-blue-800 px-2 py-0.5 rounded" data-testid={`enquiry-course-${e.id}`}>
                        {e.course}
                      </span>
                    )}
                    {e.subject && <span className="ml-2 text-xs font-normal text-slate-500">— {e.subject}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {[e.email, e.phone].filter(Boolean).join(" · ")} · {new Date(e.created_at).toLocaleString()}
                  </div>
                </div>
                <button data-testid={`enquiry-del-${e.id}`} onClick={() => delEnquiry(e.id)}
                  className="p-1.5 text-slate-400 hover:text-red-600 transition-soft" title="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{e.message}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
        <Globe size={13} /> Public website lives at <span className="font-medium text-slate-500">/</span> — admin console moved to <span className="font-medium text-slate-500">/admin</span>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-slate-700">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
